import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import { Box, Flex, Grid, Heading, IconButton } from '@radix-ui/themes';
import {
  addMonths,
  endOfMonth,
  format,
  isAfter,
  isBefore,
  isSameDay,
  lightFormat,
  startOfMonth,
  startOfToday,
} from 'date-fns';
import { IDateWidget } from 'interfaces/forms/date';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { LOCAL_DATE_FORMAT } from 'lib_ts/enums/datetime.enums';
import { RADIX, RadixBtnVariant, RadixColor } from 'lib_ts/enums/radix-ui';
import { useCallback, useMemo, useState } from 'react';

const DAYS_OF_WEEK = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

interface IProps extends IDateWidget {
  defaultDate?: Date;
  onChangeDate: (value: Date | undefined) => void;
}

export const CommonDateWidget = (props: IProps) => {
  // for styling logic
  const [today] = useState(startOfToday());

  // which month is being displayed in the widget
  const [visibleMonth, setVisibleMonth] = useState(
    startOfMonth(props.defaultDate ?? new Date())
  );

  const [selected, setSelected] = useState(props.defaultDate);

  const getColor = useCallback(
    (d: Date): RadixColor => {
      if (selected && isSameDay(d, selected)) {
        return RADIX.COLOR.ACCENT;
      }

      if (isSameDay(d, today)) {
        return RADIX.COLOR.ACCENT;
      }

      return RADIX.COLOR.NEUTRAL;
    },
    [selected, today]
  );

  const getClassName = useCallback(
    (d: Date): string | undefined => {
      if (selected && isSameDay(d, selected)) {
        return undefined;
      }

      if (isSameDay(d, today)) {
        return undefined;
      }

      return 'btn-floating';
    },
    [selected, today]
  );

  const getVariant = useCallback(
    (d: Date): RadixBtnVariant => {
      if (selected && isSameDay(d, selected)) {
        return 'solid';
      }

      if (isSameDay(d, today)) {
        return 'outline';
      }

      return 'soft';
    },
    [selected, today]
  );

  const getTooltip = useCallback(
    (d: Date): string => {
      const ds = lightFormat(d, LOCAL_DATE_FORMAT);

      if (selected && isSameDay(d, selected)) {
        return `${ds} - Selected`;
      }

      if (isSameDay(d, today)) {
        return `${ds} - Today`;
      }

      return ds;
    },
    [selected, today]
  );

  const getDisabled = useCallback(
    (d: Date): boolean => {
      if (props.minDate && isBefore(d, props.minDate)) {
        // before min
        return true;
      }

      if (props.maxDate && isAfter(d, props.maxDate)) {
        // after max
        return true;
      }

      if (
        props.allowedDates &&
        !props.allowedDates.includes(lightFormat(d, LOCAL_DATE_FORMAT))
      ) {
        // not in allowed dates
        return true;
      }

      return false;
    },
    [props.minDate, props.maxDate, props.allowedDates]
  );

  const dates = useMemo(
    () =>
      ArrayHelper.getDateOptions(
        startOfMonth(visibleMonth),
        endOfMonth(visibleMonth)
      ),
    [visibleMonth]
  );

  const disablePrevMonth = useMemo(
    () =>
      props.minDate &&
      isBefore(addMonths(visibleMonth, -1), startOfMonth(props.minDate)),
    [props.minDate, visibleMonth]
  );
  const disableNextMonth = useMemo(
    () =>
      props.maxDate &&
      isAfter(addMonths(visibleMonth, 1), endOfMonth(props.maxDate)),
    [props.maxDate, visibleMonth]
  );

  return (
    <Grid columns="7" gap="1" align="center" style={{ textAlign: 'center' }}>
      {/* header */}
      <Box>
        <IconButton
          variant="soft"
          color={RADIX.COLOR.NEUTRAL}
          disabled={disablePrevMonth}
          onClick={() => setVisibleMonth(addMonths(visibleMonth, -1))}
        >
          <ChevronLeftIcon />
        </IconButton>
      </Box>

      <Flex gridColumn="span 5" justify="center">
        <Heading mt="2" mb="0" size={RADIX.HEADING.SIZE.SM}>
          {format(visibleMonth, 'MMMM y')}
        </Heading>
      </Flex>

      <Box>
        <IconButton
          variant="soft"
          color={RADIX.COLOR.NEUTRAL}
          disabled={disableNextMonth}
          onClick={() => setVisibleMonth(addMonths(visibleMonth, 1))}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* days of week */}
      {DAYS_OF_WEEK.map((d, i) => (
        <Box key={i}>
          <IconButton
            color={RADIX.COLOR.NEUTRAL}
            className="btn-floating"
            disabled
          >
            {d}
          </IconButton>
        </Box>
      ))}

      {/* dates */}
      {dates.map((d, i) => (
        <Box key={i} gridColumn={(d.getDay() + 1).toString()}>
          <IconButton
            disabled={getDisabled(d)}
            color={getColor(d)}
            variant={getVariant(d)}
            className={getClassName(d)}
            title={getTooltip(d)}
            onClick={() => {
              setSelected(d);
              props.onChangeDate(d);
            }}
          >
            {d.getDate()}
          </IconButton>
        </Box>
      ))}
    </Grid>
  );
};
